//
//  GpxUploadTool.tsx
//
//  Lives under My Trails → Tools → GPX. Trail admins drop one or more
//  .gpx files for their trail; each gets a difficulty tier (IMBA 5-tier
//  plus our custom "Incredibly Difficult" red). The browser parses each
//  file once on file-select to derive length / elevation gain+loss /
//  bbox / point count, ships everything to the worker, and gets back a
//  row in the "Uploaded" list. iOS + Android download the raw .gpx on
//  demand and render the multi-polyline map themselves.
//
//  Two stacked sections:
//    1. Add GPX files — file picker → staged list with per-file
//       difficulty + name edit + derived stats → Upload all.
//    2. Uploaded — existing rows with inline rename / re-color /
//       delete.
//
//  Why a staged list instead of upload-on-pick: the admin almost
//  always wants to assign a difficulty before publishing. Staging
//  surfaces every file's parsed stats up front so the admin can
//  sanity-check before paying for the upload.
//

import { useEffect, useRef, useState } from "react";
import {
  type AdminAssignment,
  type GpxFileDto,
  type GpxDifficulty,
  type GpxClientMetadata,
  listAdminGpx,
  uploadGpxFile,
  updateGpxFile,
  deleteGpxFile,
  ApiError,
} from "../api";
import { parseGpxFile } from "../gpx-parser";

interface GpxUploadToolProps {
  trail: AdminAssignment;
}

interface DifficultyMeta {
  label: string;
  shortLabel: string;
  /** Polyline + chip color. Matches iOS + Android once those land. */
  color: string;
  /** IMBA shape glyph for the chip ahead of the label. */
  shape: string;
}

const DIFFICULTY_META: Record<GpxDifficulty, DifficultyMeta> = {
  easiest:               { label: "Easiest",               shortLabel: "Easiest",      color: "#FFFFFF", shape: "●"  },
  easy:                  { label: "Easy",                  shortLabel: "Easy",         color: "#5FB86B", shape: "●"  },
  more_difficult:        { label: "More Difficult",        shortLabel: "More",         color: "#4A8FD1", shape: "■"  },
  very_difficult:        { label: "Very Difficult",        shortLabel: "Very",         color: "#1A1A1A", shape: "◆"  },
  extremely_difficult:   { label: "Extremely Difficult",   shortLabel: "Extreme",      color: "#6B2D9C", shape: "◆◆" },
  incredibly_difficult:  { label: "Incredibly Difficult",  shortLabel: "Incredible",   color: "#E34C4C", shape: "!"  },
};

const DIFFICULTY_ORDER: GpxDifficulty[] = [
  "easiest",
  "easy",
  "more_difficult",
  "very_difficult",
  "extremely_difficult",
  "incredibly_difficult",
];

/** What's been picked from the file system but not yet uploaded. */
interface StagedFile {
  /** Local id — separate from the eventual server-side id. */
  localId: string;
  file: File;
  name: string;
  difficulty: GpxDifficulty;
  metadata: GpxClientMetadata;
  /** 0–100 once uploading begins; null when staged-only. */
  progress: number | null;
  error: string | null;
}

interface Feedback {
  kind: "ok" | "err";
  msg: string;
}

export function GpxUploadTool({ trail }: GpxUploadToolProps) {
  const [items, setItems] = useState<GpxFileDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [uploadingAll, setUploadingAll] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setLoadError(null);
    listAdminGpx(trail.slug)
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err instanceof ApiError ? err.message : "Couldn't load uploads."
        );
        setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [trail.slug]);

  useEffect(() => {
    if (!feedback) return;
    const t = window.setTimeout(() => setFeedback(null), 3600);
    return () => window.clearTimeout(t);
  }, [feedback]);

  async function handlePicked(files: FileList | null) {
    if (!files || files.length === 0) return;
    setParsing(true);
    const next: StagedFile[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const parsed = await parseGpxFile(file);
        next.push({
          localId: cryptoRandom(),
          file,
          name: parsed.name || stripExt(file.name),
          difficulty: "more_difficult",
          metadata: parsed.metadata,
          progress: null,
          error: null,
        });
      } catch (err) {
        setFeedback({
          kind: "err",
          msg: `${file.name}: ${err instanceof Error ? err.message : "couldn't parse"}`,
        });
      }
    }
    setStaged((cur) => [...cur, ...next]);
    setParsing(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function uploadAll() {
    if (staged.length === 0) return;
    setUploadingAll(true);
    const toUpload = staged;
    const uploaded: GpxFileDto[] = [];
    for (const s of toUpload) {
      try {
        const { item } = await uploadGpxFile(
          trail.slug,
          s.file,
          { name: s.name, difficulty: s.difficulty, metadata: s.metadata },
          (percent) => {
            setStaged((cur) =>
              cur.map((x) => (x.localId === s.localId ? { ...x, progress: percent } : x))
            );
          }
        );
        uploaded.push(item);
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : "upload failed";
        setStaged((cur) =>
          cur.map((x) =>
            x.localId === s.localId ? { ...x, error: msg, progress: null } : x
          )
        );
      }
    }
    if (uploaded.length > 0) {
      setItems((cur) => [...(cur ?? []), ...uploaded]);
      setStaged((cur) => cur.filter((x) => x.error));
      setFeedback({
        kind: "ok",
        msg:
          uploaded.length === 1
            ? "Uploaded 1 GPX."
            : `Uploaded ${uploaded.length} GPX files.`,
      });
    }
    setUploadingAll(false);
  }

  function removeStaged(localId: string) {
    setStaged((cur) => cur.filter((x) => x.localId !== localId));
  }
  function patchStaged(localId: string, patch: Partial<StagedFile>) {
    setStaged((cur) => cur.map((x) => (x.localId === localId ? { ...x, ...patch } : x)));
  }

  async function applyEdit(id: string, patch: { name?: string; difficulty?: GpxDifficulty }) {
    try {
      const { item } = await updateGpxFile(trail.slug, id, patch);
      setItems((cur) => (cur ?? []).map((x) => (x.id === id ? item : x)));
      setFeedback({ kind: "ok", msg: "Saved." });
    } catch (err) {
      setFeedback({
        kind: "err",
        msg: err instanceof ApiError ? err.message : "Save failed.",
      });
    }
  }

  async function removeUploaded(id: string, displayName: string) {
    const ok = window.confirm(`Delete "${displayName}"? This can't be undone.`);
    if (!ok) return;
    try {
      await deleteGpxFile(trail.slug, id);
      setItems((cur) => (cur ?? []).filter((x) => x.id !== id));
      setFeedback({ kind: "ok", msg: `Deleted "${displayName}".` });
    } catch (err) {
      setFeedback({
        kind: "err",
        msg: err instanceof ApiError ? err.message : "Delete failed.",
      });
    }
  }

  return (
    <div className="gpx-tool">
      {/* ─── Add section ──────────────────────────────────────────── */}
      <section className="admin-section">
        <div className="admin-section-head">
          <h3>Add GPX files</h3>
          <p className="muted">
            Pick one or more .gpx files — we'll read the route stats on your
            device, then upload. You can stage a whole system at once.
          </p>
        </div>
        <div className="admin-section-body">
          <div className="gpx-picker">
            <input
              ref={fileInputRef}
              type="file"
              accept=".gpx,application/gpx+xml,application/xml,text/xml"
              multiple
              style={{ display: "none" }}
              onChange={(e) => handlePicked(e.target.files)}
              disabled={uploadingAll || parsing}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAll || parsing}
            >
              {parsing ? "Reading…" : "Choose GPX files"}
            </button>
            {staged.length > 0 && (
              <button
                type="button"
                className="primary"
                onClick={uploadAll}
                disabled={uploadingAll}
              >
                {uploadingAll
                  ? "Uploading…"
                  : staged.length === 1
                    ? "Upload 1 file"
                    : `Upload ${staged.length} files`}
              </button>
            )}
          </div>

          {staged.length > 0 && (
            <ul className="gpx-staged-list">
              {staged.map((s) => (
                <li key={s.localId} className="gpx-staged-row">
                  <DifficultySwatch difficulty={s.difficulty} />
                  <div className="gpx-staged-main">
                    <input
                      className="gpx-name-input"
                      value={s.name}
                      onChange={(e) => patchStaged(s.localId, { name: e.target.value })}
                      disabled={uploadingAll}
                      placeholder="Trail name"
                    />
                    <div className="gpx-stat-row">
                      <Stat label="Distance" value={formatDistanceMiles(s.metadata.lengthM)} />
                      <Stat label="Gain" value={formatElevationFt(s.metadata.elevationGainM)} />
                      <Stat label="Loss" value={formatElevationFt(s.metadata.elevationLossM)} />
                      <Stat
                        label="Points"
                        value={s.metadata.pointCount?.toLocaleString() ?? "—"}
                      />
                    </div>
                    {s.progress !== null && (
                      <div className="gpx-progress">
                        <div
                          className="gpx-progress-fill"
                          style={{ width: `${s.progress}%` }}
                        />
                      </div>
                    )}
                    {s.error && <div className="gpx-row-err">{s.error}</div>}
                  </div>
                  <DifficultyPicker
                    value={s.difficulty}
                    onChange={(v) => patchStaged(s.localId, { difficulty: v })}
                    disabled={uploadingAll}
                  />
                  <button
                    type="button"
                    className="small ghost"
                    onClick={() => removeStaged(s.localId)}
                    disabled={uploadingAll}
                    title="Remove from staged"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ─── Uploaded section ─────────────────────────────────────── */}
      <section className="admin-section">
        <div className="admin-section-head">
          <h3>
            Uploaded
            {items && items.length > 0 ? ` (${items.length})` : ""}
          </h3>
          <p className="muted">
            These ride along on every iOS + Android trail screen. Rename or
            re-color anytime; the URL doesn't change.
          </p>
        </div>
        <div className="admin-section-body">
          {loadError && <div className="admin-feedback admin-feedback-err">{loadError}</div>}
          {items === null && !loadError && <div className="muted">Loading uploads…</div>}
          {items !== null && items.length === 0 && (
            <div className="muted">No GPX files yet. Add one above.</div>
          )}
          {items && items.length > 0 && (
            <ul className="gpx-uploaded-list">
              {items.map((it) => (
                <UploadedRow
                  key={it.id}
                  item={it}
                  onEdit={(patch) => applyEdit(it.id, patch)}
                  onDelete={() => removeUploaded(it.id, it.name)}
                />
              ))}
            </ul>
          )}
        </div>
      </section>

      {feedback && (
        <div
          className={
            feedback.kind === "ok"
              ? "admin-feedback admin-feedback-ok gpx-toast"
              : "admin-feedback admin-feedback-err gpx-toast"
          }
          role="status"
        >
          {feedback.msg}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function UploadedRow({
  item,
  onEdit,
  onDelete,
}: {
  item: GpxFileDto;
  onEdit: (patch: { name?: string; difficulty?: GpxDifficulty }) => void | Promise<void>;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(item.name);
  const [draftDifficulty, setDraftDifficulty] = useState<GpxDifficulty>(item.difficulty);

  function startEdit() {
    setDraftName(item.name);
    setDraftDifficulty(item.difficulty);
    setEditing(true);
  }
  async function commit() {
    const patch: { name?: string; difficulty?: GpxDifficulty } = {};
    if (draftName.trim() && draftName.trim() !== item.name) patch.name = draftName.trim();
    if (draftDifficulty !== item.difficulty) patch.difficulty = draftDifficulty;
    if (Object.keys(patch).length > 0) await onEdit(patch);
    setEditing(false);
  }

  return (
    <li className="gpx-uploaded-row">
      <DifficultySwatch difficulty={editing ? draftDifficulty : item.difficulty} />
      <div className="gpx-staged-main">
        {editing ? (
          <input
            className="gpx-name-input"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
          />
        ) : (
          <div className="gpx-uploaded-name">{item.name}</div>
        )}
        <div className="gpx-stat-row">
          <Stat label="Distance" value={formatDistanceMiles(item.lengthM)} />
          <Stat label="Gain" value={formatElevationFt(item.elevationGainM)} />
          <Stat label="Loss" value={formatElevationFt(item.elevationLossM)} />
          <Stat
            label="Points"
            value={item.pointCount?.toLocaleString() ?? "—"}
          />
        </div>
      </div>
      {editing ? (
        <DifficultyPicker
          value={draftDifficulty}
          onChange={setDraftDifficulty}
        />
      ) : (
        <div className="gpx-difficulty-readonly">
          {DIFFICULTY_META[item.difficulty].label}
        </div>
      )}
      <div className="row" style={{ gap: 6 }}>
        {editing ? (
          <>
            <button type="button" className="small primary" onClick={commit}>
              Save
            </button>
            <button type="button" className="small ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button type="button" className="small secondary" onClick={startEdit}>
              Edit
            </button>
            <button type="button" className="small ghost danger" onClick={onDelete}>
              Delete
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function DifficultySwatch({ difficulty }: { difficulty: GpxDifficulty }) {
  const meta = DIFFICULTY_META[difficulty];
  return (
    <div
      className="gpx-swatch"
      style={{ background: meta.color, color: contrastText(meta.color) }}
      title={meta.label}
    >
      {meta.shape}
    </div>
  );
}

function DifficultyPicker({
  value,
  onChange,
  disabled,
}: {
  value: GpxDifficulty;
  onChange: (v: GpxDifficulty) => void;
  disabled?: boolean;
}) {
  return (
    <select
      className="gpx-difficulty-select"
      value={value}
      onChange={(e) => onChange(e.target.value as GpxDifficulty)}
      disabled={disabled}
    >
      {DIFFICULTY_ORDER.map((d) => (
        <option key={d} value={d}>
          {DIFFICULTY_META[d].label}
        </option>
      ))}
    </select>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="gpx-stat">
      <span className="gpx-stat-value">{value}</span>
      <span className="gpx-stat-label">{label}</span>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function stripExt(name: string): string {
  return name.replace(/\.(gpx|xml)$/i, "");
}

function formatDistanceMiles(m: number | null): string {
  if (m == null) return "—";
  const miles = m / 1609.344;
  if (miles < 0.1) return `${m.toFixed(0)} m`;
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${miles.toFixed(0)} mi`;
}

function formatElevationFt(m: number | null): string {
  if (m == null) return "—";
  const ft = Math.round(m * 3.28084);
  return `${ft.toLocaleString()} ft`;
}

function contrastText(bgHex: string): string {
  const hex = bgHex.replace("#", "");
  if (hex.length !== 6) return "#000";
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // Rec.601 luminance — good enough for swatch text.
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? "#0a0a07" : "#f3f1e5";
}

function cryptoRandom(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}
